import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Check, Settings } from 'lucide-react';

const STORAGE_KEY = 'chrobe_consent_v1';

/**
 * DSGVO-compliant consent manager. Three categories:
 *   - essential (always on)
 *   - analytics (optional)
 *   - marketing  (optional)
 *
 * Stores the choice in localStorage AND fires a global "consent-change"
 * CustomEvent so analytics scripts can react. The banner returns to view
 * via window.dispatchEvent(new Event('consent:open')) — useful for the
 * footer "Cookie-Einstellungen" link.
 */
const DEFAULT_CONSENT = {
  essential: true,
  analytics: false,
  marketing: false,
  ts: null,
  v: 1,
};

export const CookieBanner = () => {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [draft, setDraft] = useState(DEFAULT_CONSENT);

  const persist = useCallback((next) => {
    const payload = { ...next, ts: new Date().toISOString(), v: 1 };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch { /* quota */ }
    setDraft(payload);
    if (typeof window !== 'undefined') {
      window.__chrobe_consent = payload;
      window.dispatchEvent(new CustomEvent('consent-change', { detail: payload }));
    }
  }, []);

  useEffect(() => {
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { /* */ }
    if (stored?.v === 1) {
      setDraft(stored);
      if (typeof window !== 'undefined') window.__chrobe_consent = stored;
      setOpen(false);
    } else {
      setOpen(true);
    }

    const onOpen = () => setOpen(true);
    window.addEventListener('consent:open', onOpen);
    return () => window.removeEventListener('consent:open', onOpen);
  }, []);

  if (!open) return null;

  const acceptAll = () => {
    persist({ essential: true, analytics: true, marketing: true });
    setOpen(false);
  };
  const rejectAll = () => {
    persist({ essential: true, analytics: false, marketing: false });
    setOpen(false);
  };
  const saveCustom = () => {
    persist({ essential: true, analytics: !!draft.analytics, marketing: !!draft.marketing });
    setOpen(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-x-2 sm:inset-x-auto sm:right-4 sm:left-4 sm:max-w-3xl sm:mx-auto bottom-2 sm:bottom-4 z-header"
        role="dialog"
        aria-label="Cookie-Einstellungen"
        data-testid="cookie-banner"
      >
        <div className="bg-[#161313] border border-[#0094ff]/30 rounded-2xl shadow-2xl p-5 sm:p-6 backdrop-blur-md">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0094ff]/15 flex items-center justify-center flex-shrink-0">
              <Cookie className="w-5 h-5 text-[#0094ff]" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-white font-bold text-base sm:text-lg">Wir verwenden Cookies</h2>
              <p className="text-xs sm:text-sm text-[#a1a1aa] mt-1 leading-relaxed">
                Wir setzen technisch notwendige Cookies, um die Plattform betreiben zu können.
                Optionale Cookies für <strong className="text-white">Analyse</strong> und <strong className="text-white">Marketing</strong> helfen uns, dein Erlebnis zu verbessern —
                du kannst deine Einwilligung jederzeit widerrufen (Footer &raquo; &bdquo;Cookie-Einstellungen&ldquo;).
                Details in unserer{' '}
                <a href="/datenschutz" className="text-[#0094ff] hover:underline" data-testid="cookie-privacy-link">Datenschutzerklärung</a>
                {' '}·{' '}
                <a href="/impressum" className="text-[#0094ff] hover:underline" data-testid="cookie-imprint-link">Impressum</a>.
              </p>
            </div>
          </div>

          <AnimatePresence initial={false}>
            {details && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-4 space-y-2">
                  <ConsentRow
                    title="Notwendig"
                    description="Erforderlich für Login, Cart und Sicherheit. Kann nicht deaktiviert werden."
                    checked
                    disabled
                    testId="consent-essential"
                  />
                  <ConsentRow
                    title="Analyse"
                    description="Anonyme Nutzungsstatistiken zur Verbesserung der Seite."
                    checked={!!draft.analytics}
                    onChange={(v) => setDraft((d) => ({ ...d, analytics: v }))}
                    testId="consent-analytics"
                  />
                  <ConsentRow
                    title="Marketing"
                    description="Personalisierte Inhalte und Tracking für Conversion-Messungen."
                    checked={!!draft.marketing}
                    onChange={(v) => setDraft((d) => ({ ...d, marketing: v }))}
                    testId="consent-marketing"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <button
              type="button"
              onClick={() => setDetails((d) => !d)}
              data-testid="cookie-details-toggle"
              className="order-3 sm:order-1 flex items-center justify-center gap-1 text-xs text-[#a1a1aa] hover:text-white px-3 py-2 rounded-lg"
            >
              <Settings className="w-3.5 h-3.5" /> {details ? 'Schließen' : 'Einstellungen'}
            </button>
            <div className="flex-1 hidden sm:block" />
            <button
              type="button"
              onClick={rejectAll}
              data-testid="cookie-reject-btn"
              className="order-2 px-4 py-2.5 text-sm rounded-lg bg-[#231f1f] border border-[#605e5e]/30 text-white hover:bg-[#2a2626]"
            >
              Nur notwendige
            </button>
            {details ? (
              <button
                type="button"
                onClick={saveCustom}
                data-testid="cookie-save-btn"
                className="order-1 sm:order-3 px-4 py-2.5 text-sm rounded-lg bg-[#0094ff] hover:bg-[#33a9ff] text-white font-semibold flex items-center justify-center gap-1.5"
              >
                <Check className="w-4 h-4" /> Auswahl speichern
              </button>
            ) : (
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-accept-btn"
                className="order-1 sm:order-3 px-4 py-2.5 text-sm rounded-lg bg-[#0094ff] hover:bg-[#33a9ff] text-white font-semibold flex items-center justify-center gap-1.5"
              >
                <Check className="w-4 h-4" /> Alle akzeptieren
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

const ConsentRow = ({ title, description, checked, disabled, onChange, testId }) => (
  <label
    className={`flex items-start gap-3 p-3 rounded-lg border ${
      checked ? 'bg-[#0094ff]/5 border-[#0094ff]/30' : 'bg-[#231f1f] border-[#605e5e]/20'
    } ${disabled ? 'opacity-90' : 'cursor-pointer'}`}
  >
    <input
      type="checkbox"
      checked={!!checked}
      disabled={!!disabled}
      onChange={(e) => onChange?.(e.target.checked)}
      className="mt-0.5 w-4 h-4 accent-[#0094ff]"
      data-testid={testId}
    />
    <div className="flex-1 min-w-0">
      <div className="text-sm text-white font-semibold">{title}</div>
      <div className="text-[11px] text-[#a1a1aa] mt-0.5">{description}</div>
    </div>
  </label>
);

export default CookieBanner;
